/**
 * Sysadmin Settings → Platform API keys (Phase 5 PR-C v1).
 *
 * Per SYSADMIN-PLATFORM-SPEC §3.9:
 *   Sysadmin API keys + scope + expiry. Distinct from tenant Forge
 *   keys (org-bound) — these are platform-scope and authorize
 *   external systems against the cross-tenant sysadmin surface.
 *
 * Key UX:
 *   - The plaintext secret is shown ONCE via <SecretRevealModal> on
 *     the create response. The modal explicitly warns "this key will
 *     not be shown again" and provides a Copy button.
 *   - Revoke routes through <TypedConfirmDialog> with the key name
 *     as confirm phrase. Revoke is non-reversible (no re-enable path).
 *   - Expired / revoked keys remain in the list for forensic context.
 *     Status pill (active / expired / revoked) makes the state obvious.
 */

import { useEffect, useMemo, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import {
  Webhook,
  Plus,
  Trash2,
  Copy,
  Check,
  KeyRound,
  ShieldAlert,
  Clock,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SectionCard } from '@/components/ui/section-card';
import { SettingField } from '@/components/ui/setting-field';
import { StatTile } from '@/components/ui/stat-tile';
import { PageError } from '@/components/ui/page-error';
import { TypedConfirmDialog } from '@/components/ui/typed-confirm-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  useSysadminPlatformApiKeys,
  useCreatePlatformApiKey,
  useRevokePlatformApiKey,
  type PlatformApiKeyRow,
  type ApiKeyScope,
  type ApiKeyCreateResponse,
} from '@/lib/use-sysadmin-settings';

export const Route = createFileRoute('/sysadmin/settings/api')({
  component: PlatformApiKeysPage,
});

// ─── Page ────────────────────────────────────────────────────────────────

function PlatformApiKeysPage() {
  const { data, isLoading, error, refetch, isFetching } =
    useSysadminPlatformApiKeys();
  const [creating, setCreating] = useState(false);
  const [revealing, setRevealing] = useState<ApiKeyCreateResponse | null>(null);
  const [revoking, setRevoking] = useState<PlatformApiKeyRow | null>(null);

  const items = data?.items ?? [];

  const counts = useMemo(
    () => ({
      total: items.length,
      active: items.filter((k) => k.status === 'active').length,
      expired: items.filter((k) => k.status === 'expired').length,
      revoked: items.filter((k) => k.status === 'revoked').length,
    }),
    [items],
  );

  if (error) {
    return (
      <div className="p-6 max-w-6xl mx-auto">
        <PageError
          title="Failed to load platform API keys"
          message={error.message}
          onRetry={refetch}
          retrying={isFetching}
        />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="p-6 max-w-6xl mx-auto">
        <div className="py-10 text-center text-muted-foreground text-sm animate-pulse">
          Loading platform API keys…
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-5">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <Webhook className="h-7 w-7 text-muted-foreground" />
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">
              Platform API keys
            </h1>
            <div className="flex items-center gap-2 mt-1">
              <Badge variant="outline" className="text-xs font-mono">§3.9</Badge>
              <Badge variant="secondary" className="text-xs">PR-C</Badge>
              <span className="text-xs text-muted-foreground">
                Sysadmin-scope keys; distinct from tenant Forge keys.
              </span>
            </div>
          </div>
        </div>
        <Button onClick={() => setCreating(true)}>
          <Plus className="h-3.5 w-3.5 mr-1.5" />
          Generate new key
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatTile label="Total" value={String(counts.total)} icon={KeyRound} tone="default" />
        <StatTile
          label="Active"
          value={String(counts.active)}
          icon={KeyRound}
          tone={counts.active > 0 ? 'success' : 'muted'}
        />
        <StatTile
          label="Expired"
          value={String(counts.expired)}
          icon={Clock}
          tone={counts.expired > 0 ? 'destructive' : 'muted'}
        />
        <StatTile
          label="Revoked"
          value={String(counts.revoked)}
          icon={ShieldAlert}
          tone="muted"
        />
      </div>

      <SectionCard
        title="Keys"
        icon={KeyRound}
        description="Sysadmin-scope API keys. Plaintext is shown once on creation."
      >
        {items.length === 0 ? (
          <div className="py-3 text-center text-sm text-muted-foreground">
            No platform API keys yet. Click "Generate new key" to create one.
          </div>
        ) : (
          <KeyTable rows={items} onRevoke={(r) => setRevoking(r)} />
        )}
      </SectionCard>

      {creating && (
        <CreateKeyDialog
          onClose={() => setCreating(false)}
          onCreated={(resp) => {
            setCreating(false);
            setRevealing(resp);
          }}
        />
      )}

      {revealing && (
        <RevealedKeyModal
          payload={revealing}
          onClose={() => setRevealing(null)}
        />
      )}

      {revoking && (
        <RevokeConfirm
          row={revoking}
          onClose={() => setRevoking(null)}
        />
      )}
    </div>
  );
}

// ─── Table ───────────────────────────────────────────────────────────────

function KeyTable({
  rows,
  onRevoke,
}: {
  rows: PlatformApiKeyRow[];
  onRevoke: (r: PlatformApiKeyRow) => void;
}) {
  return (
    <div className="rounded-md border overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-muted/50 text-xs text-muted-foreground">
          <tr>
            <th className="text-left px-3 py-2 font-medium">Name</th>
            <th className="text-left px-3 py-2 font-medium">Scope</th>
            <th className="text-left px-3 py-2 font-medium">Status</th>
            <th className="text-left px-3 py-2 font-medium">Last used</th>
            <th className="text-left px-3 py-2 font-medium">Expires</th>
            <th className="text-left px-3 py-2 font-medium">Created</th>
            <th className="text-right px-3 py-2 font-medium w-[80px]">Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={row.id}
              className={
                'border-t ' + (row.status !== 'active' ? 'opacity-60' : '')
              }
            >
              <td className="px-3 py-2 font-medium">{row.name}</td>
              <td className="px-3 py-2">
                <Badge variant="outline" className="text-[10px] font-mono">
                  {row.scope}
                </Badge>
              </td>
              <td className="px-3 py-2">
                <StatusPill status={row.status} />
              </td>
              <td className="px-3 py-2 text-xs text-muted-foreground">
                {row.lastUsedAt
                  ? new Date(row.lastUsedAt).toLocaleString()
                  : '—'}
              </td>
              <td className="px-3 py-2 text-xs text-muted-foreground">
                {new Date(row.expiresAt).toLocaleDateString()}
              </td>
              <td className="px-3 py-2 text-xs text-muted-foreground">
                {new Date(row.createdAt).toLocaleDateString()}
              </td>
              <td className="px-3 py-2 text-right">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-destructive hover:text-destructive"
                  disabled={row.status === 'revoked'}
                  onClick={() => onRevoke(row)}
                  title={
                    row.status === 'revoked' ? 'Already revoked' : 'Revoke key'
                  }
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function StatusPill({ status }: { status: 'active' | 'expired' | 'revoked' }) {
  const map: Record<typeof status, { label: string; variant: 'default' | 'secondary' | 'outline' }> = {
    active: { label: 'Active', variant: 'default' },
    expired: { label: 'Expired', variant: 'secondary' },
    revoked: { label: 'Revoked', variant: 'outline' },
  };
  const cfg = map[status];
  return (
    <Badge variant={cfg.variant} className="text-[10px]">
      {cfg.label}
    </Badge>
  );
}

// ─── Create dialog ───────────────────────────────────────────────────────

function CreateKeyDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (resp: ApiKeyCreateResponse) => void;
}) {
  const [name, setName] = useState('');
  const [scope, setScope] = useState<ApiKeyScope>('read');
  const [expiresInDays, setExpiresInDays] = useState<number>(90);

  const createMut = useCreatePlatformApiKey();

  const handleSubmit = () => {
    createMut.mutate(
      { name, scope, expiresInDays },
      {
        onSuccess: (resp) => onCreated(resp),
      },
    );
  };

  const serverError = (createMut.error as Error | null)?.message ?? null;

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Generate new platform API key</DialogTitle>
          <DialogDescription>
            The secret will be shown ONCE after creation. Copy it
            immediately; it will not be retrievable later.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <SettingField label="Name" description="Operator-facing label.">
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="h-9 text-sm"
              placeholder="e.g. cross-tenant analytics export"
            />
          </SettingField>
          <SettingField
            label="Scope"
            description="read = list/get only. write = mutations (high-risk)."
          >
            <Select
              value={scope}
              onValueChange={(v) => setScope(v as ApiKeyScope)}
            >
              <SelectTrigger className="h-9 text-sm max-w-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="read">read — list / get</SelectItem>
                <SelectItem value="write">write — mutations</SelectItem>
              </SelectContent>
            </Select>
          </SettingField>
          <SettingField
            label="Expires in"
            description="Max 365 days. No perpetual keys."
          >
            <Select
              value={String(expiresInDays)}
              onValueChange={(v) => setExpiresInDays(Number(v))}
            >
              <SelectTrigger className="h-9 text-sm max-w-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="30">30 days</SelectItem>
                <SelectItem value="90">90 days</SelectItem>
                <SelectItem value="180">180 days</SelectItem>
                <SelectItem value="365">365 days (max)</SelectItem>
              </SelectContent>
            </Select>
          </SettingField>

          {serverError && (
            <div className="text-xs text-destructive rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2">
              {serverError}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={
              createMut.isPending ||
              name.trim() === ''
            }
          >
            {createMut.isPending ? 'Generating…' : 'Generate key'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Revealed key modal ──────────────────────────────────────────────────

function RevealedKeyModal({
  payload,
  onClose,
}: {
  payload: ApiKeyCreateResponse;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    void navigator.clipboard.writeText(payload.value);
    setCopied(true);
  };

  // Reset the copied flag if the user re-opens the modal.
  useEffect(() => {
    if (!copied) return;
    const t = window.setTimeout(() => setCopied(false), 2000);
    return () => window.clearTimeout(t);
  }, [copied]);

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>API key generated — copy now</DialogTitle>
          <DialogDescription className="text-destructive">
            This key will NOT be shown again. Copy it to your secrets
            manager before closing this dialog.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <SettingField label="Name">
            <div className="text-sm font-medium">{payload.name}</div>
          </SettingField>
          <SettingField label="Scope">
            <Badge variant="outline" className="text-[10px] font-mono w-fit">
              {payload.scope}
            </Badge>
          </SettingField>
          <SettingField label="Expires">
            <div className="text-sm">
              {new Date(payload.expiresAt).toLocaleString()}
            </div>
          </SettingField>
          <SettingField
            label="Secret"
            description="Shown ONCE. Treat this string like a password."
          >
            <div className="flex items-center gap-2">
              <Input
                value={payload.value}
                readOnly
                className="h-9 text-xs font-mono"
                onFocus={(e) => e.currentTarget.select()}
              />
              <Button variant="outline" size="sm" onClick={handleCopy}>
                {copied ? (
                  <Check className="h-3.5 w-3.5 mr-1.5" />
                ) : (
                  <Copy className="h-3.5 w-3.5 mr-1.5" />
                )}
                {copied ? 'Copied' : 'Copy'}
              </Button>
            </div>
          </SettingField>

          <div className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-xs text-destructive">
            Once you close this dialog there is no way to recover this
            secret. Generate a new key if you lose it.
          </div>
        </div>

        <DialogFooter>
          <Button onClick={onClose}>I have copied the key</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Revoke confirm ──────────────────────────────────────────────────────

function RevokeConfirm({
  row,
  onClose,
}: {
  row: PlatformApiKeyRow;
  onClose: () => void;
}) {
  const revokeMut = useRevokePlatformApiKey();
  return (
    <TypedConfirmDialog
      open={true}
      onOpenChange={(o) => !o && onClose()}
      title={`Revoke "${row.name}"?`}
      description="Revokes this platform API key. Authentication using this key will fail immediately. Revoke is non-reversible — generate a new key if you need to restore access."
      consequences={[
        'Key fails all future auth checks immediately',
        'Revoke is NON-REVERSIBLE — no re-enable path',
        'Audit row is written with highRiskAction=true',
        row.lastUsedAt
          ? `Last used ${new Date(row.lastUsedAt).toLocaleString()}`
          : 'Key has never been used',
      ]}
      confirmPhrase={row.name}
      confirmLabel={revokeMut.isPending ? 'Revoking…' : 'Revoke key'}
      onConfirm={() => {
        revokeMut.mutate(
          { id: row.id, confirmPhrase: row.name },
          { onSuccess: onClose },
        );
      }}
    />
  );
}
